import React, { useMemo, useState } from "react";
import { useFormik } from "formik"
import { observer } from "mobx-react-lite";
import { useTheme } from '~/ui/styled'
import { ScrollView, DeviceEventEmitter } from "~/ui/primitives"
import { useSafeAreaInsets } from "~/ui/safe-area"
import { Flex, ColumnFlex, StartFlex, BetweenFlex, Text, Button, GradientButton } from "~components/El"
import { TextField } from "~components/InputField";
import { UpwardFundsSchema } from "~schema"
import { usePopup } from "~hooks/usePopup"
import useGroupActions from "~hooks/useGroupActions"
import { truncateDecimal, globalToast } from "~utils"
import { useStores } from "~models"

const remarks2 = [
  "• 资金将从个人钱包转入频道担保账户",
  "• 官方将按占股比例自动同步注入资金",
  "• 此操作将在频道内公开广播",
]
const UpwardFundsForm: React.FC<{
    popupId?: number
    channelChatInfo?: any
    onSuccess?: () => void
    onClose?: () => void
}> = ({ popupId, channelChatInfo, onSuccess, onClose }) => {
    const theme = useTheme()
    const insets = useSafeAreaInsets()
    const popup = usePopup()
    const { chatStore, walletStore } = useStores()
    const { onUpwardAndDownwardFunds } = useGroupActions()
    const current = chatStore.current ?? {}
    const balance = walletStore.balance
    const [loading, setLoading ] = useState(false)

    const closePopup = () => {
        onClose?.()
    }

    const formik = useFormik({
        initialValues: {
            amount: ""
        },
        validationSchema: UpwardFundsSchema,
        onSubmit: (values) => {
            if (loading) return
            if (!current.chat_id && !current.id) {
                globalToast.show("频道信息异常，请返回后重试")
                return
            }
            setLoading(true)
            onUpwardAndDownwardFunds({
                amount: values.amount,
                chat_id: current.chat_id ?? current.id,
                chat_cid: current.chat_id,
                type: 1
            }, () => {
                setLoading(false)
                globalToast.show("上压成功")
                if (popupId != null) {
                    popup.close(popupId)
                } else {
                    popup.close()
                }
                onSuccess?.()
                window.setTimeout(() => {
                    DeviceEventEmitter.emit("RefreshChannelChatInfo")
                    walletStore.getWalletInfo()
                }, 0)
            },
            (error) => {
                setLoading(false)
                globalToast.show(error?.message || "上压失败")
                window.setTimeout(() => {
                    DeviceEventEmitter.emit("RefreshChannelChatInfo")
                }, 0)
            })
        },
    });
    const { values, errors, touched, setFieldValue, handleSubmit } = formik;

    const officialAmount = useMemo(() => {
        if (!values.amount) return 0;
        let decimal = 0
        if (Number(channelChatInfo?.owner_percentage) === 0 && Number(channelChatInfo?.official_percentage) !== 0) {
            decimal = Number(values.amount) * Number(channelChatInfo?.official_percentage)
        } else if (Number(channelChatInfo?.official_percentage) === 0 && Number(channelChatInfo?.owner_percentage) !== 0) {
            decimal = 0
        } else if (Number(channelChatInfo?.owner_percentage) !== 0 && Number(channelChatInfo?.official_percentage) !== 0) {
            decimal = Number(values.amount) / Number(channelChatInfo?.owner_percentage) * Number(channelChatInfo?.official_percentage)
        };
        return truncateDecimal(decimal, 2)
    }, [values.amount])

    const totalAmount = useMemo(() => {
        if (!values.amount) return 0;
        let decimal = Number(values.amount) + Number(officialAmount)
        return truncateDecimal(decimal, 2)
    }, [values.amount, officialAmount])


    const remarks = useMemo(() => {
        return [
            `• 您的占股比例: ${channelChatInfo?.owner_percentage || 0}%`,
            `• 官方占股比例: ${channelChatInfo?.official_percentage || 0}%`,
            `• 官方将按比例同步注入资金`,
        ]
    }, [channelChatInfo])

    return (
        <ScrollView
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ paddingBottom: insets.bottom + 16 }}
        >
          <ColumnFlex
              width="100%"
              padding="12px"
              borderRadius="16px"
              bg={theme.colors.blockBackground}
          >
            <StartFlex width="100%">
               <Text fontSize="16px" color={theme.colors.text} fontWeight={600}>上压资金</Text>
            </StartFlex>
            <Flex
                flexDirection="column"
                width="100%"
                mt="10px"
                padding="10px 12px"
                borderRadius="8px"
                borderWidth="1px"
                borderColor="#fff1ed"
                borderStyle="solid"
                bg="#fff8ef"
            >
                <Text color="#9a3412" fontSize={14} fontWeight={600}>占股模式</Text>
                <Flex flexDirection="column" width="100%" mt="5px">
                    {remarks.map((content, index) => (
                        <StartFlex key={index}>
                        <Text color="#9a3412" fontSize="12px" lineHeight="18px">
                            {content}
                        </Text>
                        </StartFlex>
                    ))}
                </Flex>
            </Flex>
            <Flex flexDirection="column" width="100%">
                <TextField
                    label="注入金额"
                    value={values.amount}
                    placeholder="请输入金额"
                    keyboardType="decimal-pad"
                    onChangeText={(value) => setFieldValue("amount", value)}
                    error={touched.amount && errors.amount}
                />
                {values.amount && (
                    <Flex
                        flexDirection="column"
                        width="100%"
                        mt="10px"
                        padding="10px 12px"
                        borderRadius="8px"
                        borderWidth="1px"
                        borderColor="#cce1fc"
                        borderStyle="solid"
                        bg="#ebf3fd"
                    >
                        <Text color={theme.colors.primaryText} fontSize={14} fontWeight={600}>资金分配预览:</Text>
                        <Flex flexDirection="column" width="100%" mt="5px">
                            <Text color={theme.colors.primaryText} fontSize="12px" lineHeight="18px">
                            您的注入: ¥{values.amount}
                            </Text>
                            <Text color={theme.colors.primaryText} fontSize="12px" lineHeight="18px">
                            官方同步: ¥{officialAmount}
                            </Text>
                            <Text color={theme.colors.primaryText} fontSize="12px" lineHeight="18px">
                            总计: ¥{totalAmount}
                            </Text>
                        </Flex>
                    </Flex>
                )}
                
                <BetweenFlex
                    width="100%"
                    padding="12px"
                    borderRadius="8px"
                    bg={theme.colors.background}
                    mt="10px"
                >
                    <Text fontSize="14px" color={theme.colors.text}>钱包余额</Text>
                    <Text fontSize="14px" color={theme.colors.text} fontWeight="600">¥{balance}</Text>
                </BetweenFlex>
            </Flex>
            <Flex
                flexDirection="column"
                width="100%"
                mt="10px"
                padding="10px 12px"
                borderRadius="8px"
                bg={theme.colors.background}
            >
                <Flex flexDirection="column" width="100%">
                    {remarks2.map((content, index) => (
                        <StartFlex key={index}>
                        <Text color={theme.colors.secondary} fontSize="12px" lineHeight="18px">
                            {content}
                        </Text>
                        </StartFlex>
                    ))}
                </Flex>
            </Flex>
            <BetweenFlex width="100%" padding="12px 0">
                <Button
                    width="48%"
                    height="44px"
                    borderRadius={theme.radii.lg}
                    bg={theme.colors.background}
                    onClick={closePopup}
                >
                    <Text color={theme.colors.infoText} fontSize={16}>取消</Text>
                </Button>
                <GradientButton
                    width="48%"
                    height={44}
                    colors={['#fb994d', '#ee4b49']}
                    borderRadius={8}
                    loading={loading}
                    disabled={loading}
                    onClick={() => handleSubmit()}
                >
                    <Text color={theme.colors.textLight} fontSize={16}>确认注入</Text>
                </GradientButton>
            </BetweenFlex>
          </ColumnFlex>
        </ScrollView>
    )
}

export default observer(UpwardFundsForm)
